/** The block rate against its target, with its coverage beside it (L1).
 *
 *  A ratio against a limit is a `Meter` (dashboard design rules §2), not a
 *  gauge and not a two-slice donut. Two meters sit here, and the second one is
 *  the point: a percentage computed from partial data has to show its coverage
 *  (§5), because "99.9% over 90 days" and "99.9% over the four days we managed
 *  to collect" are different claims and only one of them belongs on a slide.
 *
 *  The meter is never colour-alone — the tone is paired with a `Badge` naming
 *  the state and with the raw numerator and denominator underneath.
 */

import { Badge, Meter, Typography } from "@norican/react";

import type { Verdict } from "@/components/board";
import type { SecuritySummary } from "@/lib/api";

import { RATE_ANCHOR } from "./exec-tiles";
import { PanelCard } from "./panel-card";
import {
  blockRateTone,
  coverageTone,
  formatCount,
  formatPct,
  formatRate,
  meterTone,
  type StatusTone,
} from "./status";

/** The claim the quarterly deck makes. The meter is read against this. */
const TARGET_PCT = 99.9;

interface BlockRateMeterProps {
  summary: SecuritySummary;
}

export function BlockRateMeter({ summary }: BlockRateMeterProps) {
  const rate = summary.block_rate_pct;
  const rateTone = blockRateTone(rate);
  const coverage = summary.coverage_pct;
  const coverageState = coverageTone(coverage);
  const considered = summary.blocked + summary.delivered;

  return (
    <section id={RATE_ANCHOR} className="scroll-mt-6">
      <PanelCard
        title="Block rate"
        caption={`Blocked ÷ (blocked + delivered), last ${summary.window_days} days`}
        verdict={rateVerdict(rate, coverageState)}
      >
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Meter.Root value={rate ?? 0} min={0} max={100} tone={meterTone(rateTone)}>
              <div className="flex items-baseline justify-between gap-2">
                <Meter.Label>Measured rate</Meter.Label>
                <Typography variant="heading-3" as="span" tabular>
                  {formatRate(rate)}
                </Typography>
              </div>
              <Meter.Track>
                <Meter.Indicator />
              </Meter.Track>
            </Meter.Root>
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={rateTone} variant="subtle" size="sm">
                {rate === null || rate === undefined
                  ? "Not measurable"
                  : rate >= TARGET_PCT
                    ? `At or above the ${TARGET_PCT}% claim`
                    : `Below the ${TARGET_PCT}% claim`}
              </Badge>
              <Typography variant="body-sm" tone="muted">
                {formatCount(summary.blocked)} of {formatCount(considered)} dangerous messages
                stopped
              </Typography>
            </div>
          </div>

          {/* Coverage beside the number, not under it. A rate over four days
              is not the same claim as a rate over ninety. */}
          <div className="flex flex-col gap-2">
            <Meter.Root value={coverage ?? 0} min={0} max={100} tone={meterTone(coverageState)}>
              <div className="flex items-baseline justify-between gap-2">
                <Meter.Label>Coverage of the window</Meter.Label>
                <Typography variant="heading-3" as="span" tabular>
                  {formatPct(coverage)}
                </Typography>
              </div>
              <Meter.Track>
                <Meter.Indicator />
              </Meter.Track>
            </Meter.Root>
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={coverageState} variant="subtle" size="sm">
                {summary.days_with_data} of {summary.window_days} days collected
              </Badge>
              <Typography variant="body-sm" tone="muted">
                {coverageState === "success"
                  ? "The rate is computed over essentially the whole window."
                  : "The rate beside it is computed over the collected days only — read it as partial."}
              </Typography>
            </div>
          </div>
        </div>
      </PanelCard>
    </section>
  );
}

/** What the rate means for the deck's claim, in one line. */
function rateVerdict(rate: number | null | undefined, coverageState: StatusTone): Verdict {
  if (rate === null || rate === undefined) {
    return {
      label: "No data",
      tone: "neutral",
      text: "Nothing has been collected, so the deck's 99.9% claim cannot be checked yet.",
    };
  }
  if (coverageState === "danger") {
    return {
      label: "Partial",
      tone: "warning",
      text: "Too little of the window was collected to put this figure on a slide. Fix collection before quoting it.",
    };
  }
  if (rate >= TARGET_PCT) {
    return {
      label: "Holding",
      tone: "success",
      text: `The ${TARGET_PCT}% claim in the deck is supported by the measurement. No decision needed.`,
    };
  }
  return {
    label: "Act",
    tone: "danger",
    text: `The measured rate is below the ${TARGET_PCT}% the deck claims — either the claim moves or the control does.`,
  };
}
